import { useEffect, useState, useRef } from 'react'

import { Button } from 'ui/button'

interface ApprovalAnimationProps {
  onComplete: () => void
  onUndo?: () => void
  duration?: number // in milliseconds
  showNextPostButton?: boolean
  onNextPostClick?: () => void
}

const ApprovalAnimation = ({
  onComplete,
  onUndo,
  duration = 3000,
  showNextPostButton = false,
  onNextPostClick,
}: ApprovalAnimationProps) => {
  const [progress, setProgress] = useState(100) // 100 → 0 over `duration`
  const [timeLeft, setTimeLeft] = useState(Math.ceil(duration / 1000))
  const onCompleteRef = useRef(onComplete)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Keep the ref updated
  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    const startTime = Date.now()

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime
      const newProgress = Math.max(100 - (elapsed / duration) * 100, 0)
      const newTimeLeft = Math.max(Math.ceil((duration - elapsed) / 1000), 0)

      setProgress(newProgress)
      setTimeLeft(newTimeLeft)

      if (elapsed >= duration) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
        onCompleteRef.current()
      }
    }, 50) // Update every 50ms for smooth animation

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [duration]) // Only depend on duration

  const handleUndo = () => {
    // Stop the animation timer
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    // Call the undo callback
    if (onUndo) {
      onUndo()
    }
  }

  return (
    <div className="flex flex-col">
      {/* Mirrors the resolved-state banner (PostAction): green accent bar +
          circle-check + left-aligned "Approved!" + outline Undo, so the
          auto-finalise countdown looks like the state it resolves into. A green
          fill recedes behind the content over `duration` as the running
          countdown, alongside the "Ns" text. */}
      <div className="relative flex w-full items-center gap-3 overflow-hidden rounded-lg bg-green-50 p-2 lg:max-w-[360px]">
        {/* Draining countdown fill — recedes behind the content as time runs */}
        <div
          className="absolute inset-y-0 left-0 bg-green-200 transition-all duration-75 ease-linear motion-reduce:transition-none"
          style={{ width: `${progress}%` }}
          aria-hidden="true"
        />
        <div
          className="relative z-10 w-0.5 shrink-0 self-stretch rounded-[3px] bg-green-600"
          aria-hidden="true"
        />
        <div className="relative z-10 flex min-w-0 flex-1 items-center gap-2 py-1">
          <i
            className="fa-regular fa-circle-check flex size-5 shrink-0 items-center justify-center self-center text-base leading-none text-green-600"
            aria-hidden="true"
          />
          <p className="m-0 flex-1 font-inter text-sm font-medium leading-5 text-neutral-800">
            Approved!
          </p>
        </div>

        <div className="relative z-10 flex shrink-0 items-center gap-2 self-center">
          <span className="text-[12px] font-[500] leading-[16px] text-neutral-500">
            {timeLeft}s
          </span>
          <Button
            variant="outline"
            size="sm"
            className="mr-2 text-[12px] font-[500]"
            onClick={handleUndo}
          >
            Undo
          </Button>
        </div>
      </div>

      {/* Next pending post button */}
      {showNextPostButton && (
        <button
          className="w-[360px] h-[56px] mt-[16px] flex items-center justify-center gap-[8px] bg-[#F6B324] text-[#ffffff] rounded-[8px] font-[Inter] text-[16px] font-[600] cursor-pointer border-none"
          onClick={onNextPostClick}
        >
          Next pending post
          <i className="fa-regular fa-chevron-right text-[12px]" />
        </button>
      )}
    </div>
  )
}

export default ApprovalAnimation
